import { Grid } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useFormContext } from 'react-hook-form';
import { SimpleCheckbox } from '@AMIEWEB/Unit/Common/SimpleCheckbox';
import { SingleLineInput } from '@AMIEWEB/Unit/Common/SingleLineInput';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  fieldStyle: {
    '& .MuiFilledInput-root': {
      backgroundColor: `${theme.palette.system.grayChip} !important`,
    },
    '&:hover .MuiFilledInput-root': {
      backgroundColor: `${theme.palette.system.silver} !important`,
    },
    '&:hover .MuiInputLabel-root': {
      color: `${theme.palette.system.primary} !important`,
    },
    '& .Mui-disabled .MuiFilledInput-input': {
      visibility: 'hidden',
    },
    '& .MuiFilledInput-input': {
      paddingTop: '20px',
    },
    '& .MuiFormLabel-filled': {
      font: '12px Roboto',
    },
  },
}));

export const AdditionalInformationItem = ({
  checkboxName,
  textFieldName,
  label,
}: {
  checkboxName: string;
  textFieldName: string;
  label: string;
}) => {
  const { classes } = useStyles();
  const { control, setValue, watch } = useFormContext();
  const isCheckboxSelected = watch(checkboxName);

  return (
    <Grid item xs={6} display={'flex'} flexDirection={'row'} alignItems={'center'}>
      <Grid item xs={6}>
        <Controller
          name={checkboxName}
          control={control}
          render={({ field }) => (
            <SimpleCheckbox
              sx={{ width: 18, height: 18 }}
              isChecked={field.value}
              handleChecked={event => {
                field.onChange(event.target.checked);
                if (!event.target.checked) setValue(textFieldName, '');
              }}
              checkboxLabel={label}
              checkboxName={checkboxName}
              id={checkboxName}
            />
          )}
        />
      </Grid>
      {isCheckboxSelected && (
        <Grid item xs={6}>
          <Controller
            name={textFieldName}
            control={control}
            render={({ field }) => (
              <SingleLineInput
                id={textFieldName}
                inputDefaultValue={''}
                inputLabel={label}
                inputName={textFieldName}
                inputValue={field.value}
                handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  field.onChange(event.target.value);
                }}
                fieldStyle={classes.fieldStyle}
              />
            )}
          />
        </Grid>
      )}
    </Grid>
  );
};

export const AdditionalInformation = () => {
  const { t } = useTranslation();
  const { classes } = useStyles();

  const { control } = useFormContext();

  return (
    <Grid container spacing={2} columnSpacing={6}>
      <AdditionalInformationItem
        checkboxName="additionalInformation.holidays.isSelected"
        label={t('order.newOrderCreation.orderDetails.additionalInformation.holidays')}
        textFieldName="additionalInformation.holidays.selectedValue"
      />
      <AdditionalInformationItem
        checkboxName="additionalInformation.weekends.isSelected"
        label={t('order.newOrderCreation.orderDetails.additionalInformation.weekends')}
        textFieldName="additionalInformation.weekends.selectedValue"
      />
      <AdditionalInformationItem
        checkboxName="additionalInformation.onCall.isSelected"
        label={t('order.newOrderCreation.orderDetails.additionalInformation.onCall')}
        textFieldName="additionalInformation.onCall.selectedValue"
      />
      <AdditionalInformationItem
        checkboxName="additionalInformation.float.isSelected"
        label={t('order.newOrderCreation.orderDetails.additionalInformation.float')}
        textFieldName="additionalInformation.float.selectedValue"
      />
      <AdditionalInformationItem
        checkboxName="additionalInformation.allowBlocks.isSelected"
        label={t('order.newOrderCreation.orderDetails.additionalInformation.allowBlocks')}
        textFieldName="additionalInformation.allowBlocks.selectedValue"
      />
      <AdditionalInformationItem
        checkboxName="additionalInformation.noTimeOffAllowed.isSelected"
        label={t('order.newOrderCreation.orderDetails.additionalInformation.noTimeOffAllowed')}
        textFieldName="additionalInformation.noTimeOffAllowed.selectedValue"
      />
      <Grid item xs={6}>
        <Controller
          name={'additionalInformation.scrubColors'}
          control={control}
          render={({ field }) => (
            <SingleLineInput
              id={'additionalInformation.scrubColors'}
              inputDefaultValue={''}
              inputLabel={t('order.newOrderCreation.orderDetails.additionalInformation.scrubColors')}
              inputName={'additionalInformation.scrubColors'}
              inputValue={field.value}
              handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                field.onChange(event.target.value);
              }}
              fieldStyle={classes.fieldStyle}
            />
          )}
        />
      </Grid>
    </Grid>
  );
};
